import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProtectedAction } from '../hooks/useProtectedAction';
import { useNavigationStack } from '../contexts/NavigationContext';
import './Auth.css';

const Auth = () => {
  const navigate = useNavigate();
  const { navigateWithTransition } = useNavigationStack();
  const location = useLocation();
  const { loginWithGoogle, currentUser, loading } = useAuth();
  const { resumeIntendedAction } = useProtectedAction();

  const [error, setError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  // Get intended action message from navigation state
  const message = location.state?.message;

  // If already logged in, redirect
  useEffect(() => {
    console.log('[Auth] State changed:', { loading, hasUser: !!currentUser });
    if (!loading && currentUser) {
      console.log('[Auth] User already signed in, resuming intended action');
      resumeIntendedAction();
    }
  }, [currentUser, loading, resumeIntendedAction]);

  const handleGoogleSignIn = async () => {
    console.log('[Auth] Google sign-in initiated');
    try {
      setError(null);
      setSigningIn(true);

      console.log('[Auth] Calling loginWithGoogle...');
      const result = await loginWithGoogle();
      console.log('[Auth] Google sign-in successful:', { uid: result?.user?.uid });

      console.log('[Auth] Resuming intended action...');
      resumeIntendedAction();
    } catch (err) {
      console.error('[Auth] Google sign-in failed:', {
        message: err.message,
        code: err.code,
        fullError: err
      });

      // Handle specific error cases
      if (err.message.includes('not configured')) {
        console.warn('[Auth] Firebase not configured');
        setError('Authentication is not configured. Please contact support.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        console.log('[Auth] User cancelled sign-in popup');
        setError('Sign-in cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        console.warn('[Auth] Popup blocked by browser');
        setError('Pop-up blocked by browser. Please enable pop-ups for this site.');
      } else {
        console.error('[Auth] Unexpected error during sign-in');
        setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setSigningIn(false);
      console.log('[Auth] Sign-in process completed');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Link to="/" className="auth-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Home
        </Link>

        <div className="auth-content">
          <h1 className="auth-title">Welcome to Fantomize</h1>
          <p className="auth-subtitle">
            {message || 'Sign in to save your favorite looks and get personalized recommendations'}
          </p>

          {/* Error Message */}
          {error && (
            <div className="auth-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {/* Google Sign-In Button */}
          <button
            className="google-signin-btn"
            onClick={handleGoogleSignIn}
            disabled={signingIn}
          >
            {signingIn ? (
              <>
                <div className="signin-spinner"></div>
                Signing in...
              </>
            ) : (
              <>
                <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Auth;


